import type { TransactionList } from "@/client";
import { MetricStrip } from "@/components/MetricStrip";
import { Checks } from "@/domain/checks";
import { OperatingExpenses } from "@/domain/operating-expenses";
import { SupplierPayments } from "@/domain/supplier-payments";
import { FinancePresentation } from "@/presentation/finance";

import { SupplierBarChart } from "@/features/dashboard/SupplierBarChart";

export function OverviewView({ data }: { data: TransactionList }) {
	const supplierPaymentTotals = SupplierPayments.summarize(data.transactions);
	const sugarPaymentTotals = SupplierPayments.summarize(data.transactions, "sugar");
	const packagingPaymentTotals = SupplierPayments.summarize(data.transactions, "packaging");
	const freightPaymentTotals = SupplierPayments.summarize(data.transactions, "inbound_freight");
	const checkSummary = Checks.summarize(data.transactions);
	const operatingExpenseSummary = OperatingExpenses.summarize(data.transactions);
	const operatingExpenseTotalCents = operatingExpenseSummary.reduce(
		(total, category) => total + category.amountCents,
		0,
	);
	const operatingExpenseCount = operatingExpenseSummary.reduce(
		(total, category) => total + category.transactionCount,
		0,
	);
	const recognizedPaymentIds = new Set([
		...data.transactions
			.filter((transaction) => transaction.direction === "DEBIT" && transaction.supplier)
			.map((transaction) => transaction.id),
		...checkSummary.movements
			.filter(
				(movement) =>
					movement.kind !== "RETURNED" &&
					movement.checkDestination?.destinationId === "OUTBOUND_FREIGHT",
			)
			.map((movement) => movement.id),
		...OperatingExpenses.list(data.transactions).map((transaction) => transaction.id),
	]);
	const recognizedPayments = data.transactions.filter((transaction) =>
		recognizedPaymentIds.has(transaction.id),
	);
	const recognizedPaymentTotalCents = recognizedPayments.reduce(
		(total, transaction) => total + Math.abs(transaction.amountCents),
		0,
	);
	const recognizedPaymentCount = recognizedPayments.length;
	const sugarPaymentTotalCents = sugarPaymentTotals.reduce(
		(total, supplier) => total + supplier.amountCents,
		0,
	);
	const freightPaymentTotalCents = freightPaymentTotals.reduce(
		(total, supplier) => total + supplier.amountCents,
		0,
	);
	const packagingPaymentTotalCents = packagingPaymentTotals.reduce(
		(total, supplier) => total + supplier.amountCents,
		0,
	);
	const sugarPaymentCount = sugarPaymentTotals.reduce(
		(total, supplier) => total + supplier.transactionCount,
		0,
	);
	const freightPaymentCount = freightPaymentTotals.reduce(
		(total, supplier) => total + supplier.transactionCount,
		0,
	);
	const packagingPaymentCount = packagingPaymentTotals.reduce(
		(total, supplier) => total + supplier.transactionCount,
		0,
	);

	return (
		<>
			<section className="mb-6 overflow-hidden rounded-[1.75rem] border border-emerald-950/10 bg-emerald-950 text-white shadow-[0_24px_70px_-38px_rgba(6,78,59,0.8)]">
				<div className="grid gap-5 px-6 py-6 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-end lg:px-7">
					<div>
						<p className="text-xs font-semibold uppercase tracking-[0.16em] text-emerald-200">
							Pagamentos reconhecidos
						</p>
						<p className="mt-2 text-3xl font-semibold tracking-[-0.04em] sm:text-4xl">
							{FinancePresentation.money(recognizedPaymentTotalCents)}
						</p>
						<p className="mt-2 text-sm text-emerald-100/75">
							{recognizedPaymentCount} pagamentos identificados no mês
						</p>
					</div>
					<div className="sm:text-right">
						<p className="text-3xl font-semibold tabular-nums">{supplierPaymentTotals.length}</p>
						<p className="mt-1 max-w-52 text-xs leading-5 text-emerald-100/75">
							fornecedores pagos reconhecidos
						</p>
					</div>
				</div>
				<div className="border-t border-white/10 bg-white">
					<div className="px-4 pt-3 text-[10px] font-semibold uppercase tracking-[0.14em] text-stone-400">
						Principais componentes reconhecidos
					</div>
					<div className="p-4">
						<MetricStrip
							items={[
								{
									label: "Açúcar",
									value: FinancePresentation.money(sugarPaymentTotalCents),
									detail: `${sugarPaymentCount} pagamentos`,
								},
								{
									label: "Embalagens",
									value: FinancePresentation.money(packagingPaymentTotalCents),
									detail: `${packagingPaymentCount} pagamentos`,
								},
								{
									label: "Frete de entrada",
									value: FinancePresentation.money(freightPaymentTotalCents),
									detail: `${freightPaymentCount} pagamentos`,
								},
								{
									label: "Frete de saída",
									value: FinancePresentation.money(checkSummary.outboundFreight.amountCents),
									detail: `${checkSummary.outboundFreight.count} cheques`,
									tone: "success",
								},
								{
									label: "Despesas operacionais",
									value: FinancePresentation.money(operatingExpenseTotalCents),
									detail: `${operatingExpenseCount} pagamentos`,
									tone: "success",
								},
							]}
						/>
						<p className="mt-2 text-[11px] text-stone-500">
							Os componentes destacam recortes operacionais e não formam, isoladamente, o total
							reconhecido.
						</p>
					</div>
				</div>
			</section>

			<SupplierBarChart items={supplierPaymentTotals} title="Pagamentos por fornecedor" />
		</>
	);
}
